import _ from 'lodash';
import { getPrice } from './getPrice';
import { formatPrice } from './formatPrice';

/**
 * @memberof module:util
 * @function
 * @name getPickupDeliveryErsOptionData
 * @description It provides total price for pickup, ers and delivery view-details options,
 * whether pickup, ers or delivery option should be hidden or not, options array in pickup/ers/delivery mode, 
 * table data array for selected one pickup/ers/delivery option in FCL/LCL/AIR types.
 *
 * @param {string} mode - It's either pickup or delivery or ers
 * @param {number} selectedOption - It's the is selected radio option
 * @param {Object} itemJson - single rate card
 * @param {number} chargeableWeight - Either it's the chargeable weight for FCL/LCL/AIR or
 * the number of containers in a single container type for FCL.
 * @returns {Object}
 *
 * @example
 * console.log(getPickupDeliveryErsOptionData(...)); 
 * 
 * //output
 * {
    rate: string //"400.25",
    modeIsHidden: boolean,
    selectedOption: number,
    options: [...],
    tableDataArr: [...],
 * }
 */

// mode is pickup or delivery or ers
// selectedOption is selected radio option
// chargeableWeight for fcl or lcl or air
//return an object
//selectedOption is the array index starting from zero
export const getPickupDeliveryErsOptionData = (
  mode,
  selectedOption,
  itemJson,
  chargeableWeight,
  options = {}
) => {
  const { modeIsHidden, options: allOptions = [] } = getAllOptions(
    mode,
    itemJson,
    chargeableWeight
  );

  // check if selected index greater than array index set to zero
  selectedOption = selectedOption > allOptions.length - 1 ? 0 : selectedOption;

  let tableDataArr = getTableDataArr(
    mode,
    itemJson,
    chargeableWeight,
    selectedOption,
    options
  );

  let finalOptionsData = {
    rate: (allOptions[selectedOption] && allOptions[selectedOption].rate) || 0,
    modeIsHidden,
    selectedOption,
    options: allOptions,
    tableDataArr,
  };

  return finalOptionsData;
};

// get all options
const getAllOptions = (mode, itemJson, chargeableWeight) => {
  const modeSection = getSection(mode);
  let modeIsHidden = false;
  let allOptions = [];

  if (
    Array.isArray(itemJson[modeSection]) &&
    itemJson[modeSection].length !== 0
  ) {
    itemJson[modeSection].map((option) => {
      option = option || {};
      let rate =
        Array.isArray(option.rate) &&
        option.rate.map((rateItem) => {
          if (!Array.isArray(rateItem)) return 0;
          // rateItem[11] is the pre-computed total stamped by enhancePostalApiCharges for
          // API-fetched charges. For locally-initialised options (e.g. ERS) that only
          // carry a per-unit rate at [2], fall back to that so totals are never 0.
          let tempRate = rateItem[11] != null ? getPrice(rateItem[11]) : getPrice(rateItem[2]);

          // //check for multiply Boolean
          // if (rateItem[3]) {
          //   // do multiply
          //   tempRate = tempRate * chargeableWeight;
          // }

          // for minimum rate, define min. check after multiply check
          if (rateItem[4]) {
            tempRate =
              tempRate < getPrice(rateItem[4])
                ? getPrice(rateItem[4])
                : tempRate;
          }

          // if chargeableWeight is zero, when user has not selected anything
          if (chargeableWeight === 0) {
            tempRate = 0;
          }

          return tempRate;
        });

      rate = _.sum(rate);
      rate = getPrice(rate);

      let tempOption = {
        id: option.id || '',
        name: option.name || 'N/A',
        rate: option.fetch_zip_code ? option.rate : formatPrice(getPrice(rate)), //add rate array for fetch zip code = true instead of rate price
        description: option.description || '',
        fetch_zip_code: option.fetch_zip_code || false,
        show_variable_rate: !!option.show_variable_rate,
      };

      allOptions.push(tempOption);
    });
  } else {
    modeIsHidden = true;
  }
  if (allOptions.length < 1) {
    modeIsHidden = true;
  }

  // console.log({ modeIsHidden, options: allOptions });

  return { modeIsHidden, options: allOptions };
};

// get tableDataArr
const getTableDataArr = (mode, itemJson, chargeableWeight, selectedOption, options = {}) => {
  const modeSection = getSection(mode);

  let tableDataArr = [];

  if (
    Array.isArray(itemJson[modeSection]) &&
    itemJson[modeSection].length !== 0
  ) {
    let selectedOptionObj = itemJson[modeSection][selectedOption] || {};
    let selectedOptionRateArr = selectedOptionObj.rate;
    selectedOptionRateArr = Array.isArray(selectedOptionRateArr)
      ? selectedOptionRateArr
      : [];
    selectedOptionRateArr.map((rateItem) => {
      if (!Array.isArray(rateItem)) return [];
      let tempRate = rateItem[11] != null ? getPrice(rateItem[11]) : getPrice(rateItem[2]);

      // //check for multiply Boolean
      // if (rateItem[3]) {
      //   // do multiply
      //   tempRate = tempRate * chargeableWeight;
      // }

      // for minimum rate, define min. check after multiply check
      if (rateItem[4]) {
        tempRate =
          tempRate < getPrice(rateItem[4]) ? getPrice(rateItem[4]) : tempRate;
      }

      // if chargeableWeight is zero, when user has not selected anything
      if (chargeableWeight === 0) {
        tempRate = 0;
      }

      tempRate = getPrice(tempRate);

      // format price, include insignificant trailing zeros
      tempRate = formatPrice(tempRate);

      tableDataArr.push([rateItem[0], rateItem[1], tempRate]);
      return tempRate;
    });
  }

  return tableDataArr;
};

// get section
const getSection = (mode) => {
  let modeSection;
  if (mode === PICKUP) {
    modeSection = PICKUP_SECTION;
  } else if (mode === ERS) {
    modeSection = ERS_SECTION;
  } else if (mode === DELIVERY) {
    modeSection = DELIVERY_SECTION;
  }
  return modeSection;
};

// mode
const PICKUP = 'pickup';
const DELIVERY = 'delivery';
const ERS = 'ers';

// for json, mode section
const PICKUP_SECTION = 'pickup_section';
const DELIVERY_SECTION = 'delivery_section';
const ERS_SECTION = 'ers_section';
